import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { apiGet, apiPatch } from '../../../../utils/api';
import { GitBranch, Crown, User, ChevronRight } from 'lucide-react';
import { toast } from 'react-hot-toast';

const ClientHierarchyPanel = ({ clientId }) => {
    const { theme } = useTheme();
    const dark = theme === 'dark';
    const cardBg = dark ? 'bg-gray-800' : 'bg-white';
    const borderColor = dark ? 'border-gray-700' : 'border-gray-200';

    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { fetchHierarchy(); }, [clientId]);

    const fetchHierarchy = async () => {
        try {
            const res = await apiGet(`/client_manager/hierarchy${clientId ? `?client_id=${clientId}` : ''}`);
            if (res.ok) {
                const data = await res.json();
                setMembers(data.hierarchy);
            }
        } catch {} finally { setLoading(false); }
    };

    const handleAssignReportsTo = async (memberId, reportsToId) => {
        try {
            const res = await apiPatch(`/client_manager/users/${memberId}/assign_reports_to`, {
                reports_to_id: reportsToId || null
            });
            if (res.ok) {
                toast.success('Updated');
                fetchHierarchy();
            } else {
                const data = await res.json();
                toast.error(data.error || 'Failed to update');
            }
        } catch { toast.error('Failed to update'); }
    };

    // Build tree structure
    const buildTree = () => {
        const byId = {};
        members.forEach(m => { byId[m.id] = { ...m, children: [] }; });

        const roots = [];
        members.forEach(m => {
            if (m.reports_to_id && byId[m.reports_to_id]) {
                byId[m.reports_to_id].children.push(byId[m.id]);
            } else {
                roots.push(byId[m.id]);
            }
        });

        // Sort roots: client_manager first, then by hierarchy_level desc
        roots.sort((a, b) => {
            if (a.client_role === 'client_manager') return -1;
            if (b.client_role === 'client_manager') return 1;
            return (b.hierarchy_level || 0) - (a.hierarchy_level || 0);
        });

        return roots;
    };

    const possibleManagers = (currentMember) => {
        return members.filter(m => {
            if (m.id === currentMember.id) return false;
            if (m.client_role === 'client_manager') return true;
            if (m.hierarchy_level && currentMember.hierarchy_level) {
                return m.hierarchy_level > currentMember.hierarchy_level;
            }
            return false;
        });
    };

    const TreeNode = ({ node, depth = 0 }) => (
        <div>
            <div className={`flex items-center gap-2 py-2 px-3 rounded-lg ${dark ? 'hover:bg-gray-750' : 'hover:bg-gray-50'}`}
                 style={{ marginLeft: depth * 28 }}>
                {depth > 0 && (
                    <div className="flex items-center text-gray-400">
                        <ChevronRight size={12} />
                    </div>
                )}
                {node.client_role === 'client_manager'
                    ? <Crown size={16} className="text-orange-500 flex-shrink-0" />
                    : <User size={16} className="text-gray-400 flex-shrink-0" />
                }
                <span className="font-rasa text-sm font-medium">
                    {node.first_name} {node.last_name}
                </span>
                <span className="text-xs text-gray-500 font-rasa">@{node.username}</span>
                {node.client_role === 'client_manager' ? (
                    <span className="px-2 py-0.5 rounded text-xs font-rasa bg-orange-100 text-orange-800">Manager</span>
                ) : node.client_role_name ? (
                    <span className="px-2 py-0.5 rounded text-xs font-rasa text-white" style={{ backgroundColor: node.client_role_color || '#6B7280' }}>
                        {node.client_role_name}
                    </span>
                ) : (
                    <span className={`px-2 py-0.5 rounded text-xs font-rasa ${dark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>No Role</span>
                )}
                {node.direct_report_count > 0 && (
                    <span className="text-xs text-gray-400 font-rasa">{node.direct_report_count} report{node.direct_report_count > 1 ? 's' : ''}</span>
                )}

                {/* Reports-to selector for non-manager users */}
                {node.client_role !== 'client_manager' && (
                    <select
                        value={node.reports_to_id || ''}
                        onChange={e => handleAssignReportsTo(node.id, e.target.value)}
                        className={`ml-auto text-xs font-rasa px-2 py-1 rounded border ${borderColor} ${dark ? 'bg-gray-700' : 'bg-white'}`}
                    >
                        <option value="">No manager</option>
                        {possibleManagers(node).map(m => (
                            <option key={m.id} value={m.id}>
                                {m.first_name} {m.last_name}{m.client_role_name ? ` (${m.client_role_name})` : m.client_role === 'client_manager' ? ' (Manager)' : ''}
                            </option>
                        ))}
                    </select>
                )}
            </div>
            {node.children.sort((a, b) => (b.hierarchy_level || 0) - (a.hierarchy_level || 0)).map(child => (
                <TreeNode key={child.id} node={child} depth={depth + 1} />
            ))}
        </div>
    );

    if (loading) return <div className="text-center py-8 text-gray-500 font-rasa">Loading...</div>;

    const tree = buildTree();

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-lg font-dm-serif">Organization Hierarchy</h2>
                <p className="text-sm text-gray-500 font-rasa mt-1">View and manage the reporting structure</p>
            </div>

            {members.length === 0 ? (
                <div className={`text-center py-8 rounded-lg ${cardBg} border ${borderColor}`}>
                    <GitBranch className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-gray-500 font-rasa">No team members yet</p>
                </div>
            ) : (
                <div className={`rounded-lg border ${borderColor} ${cardBg} p-4`}>
                    {tree.map(node => (
                        <TreeNode key={node.id} node={node} depth={0} />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ClientHierarchyPanel;
